import React, { useState, useEffect, useRef } from 'react';
import { Worker, Viewer, SpecialZoomLevel } from '@react-pdf-viewer/core';
import { pageNavigationPlugin } from '@react-pdf-viewer/page-navigation';
import { searchPlugin, RenderSearchProps } from '@react-pdf-viewer/search';
import styles from './PDFViewer.module.scss';
import { Button, TextInput } from '../../UI';
import { IconCaretLeft, IconCaretRight, IconSearch } from '../../Icon';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/page-navigation/lib/styles/index.css';
import '@react-pdf-viewer/search/lib/styles/index.css';

// Extract the first few words from a text string
const extractFirstWords = (text: string, wordCount: number): string => {
  if (!text) return '';
  const words = text.trim().split(/\s+/);
  return words.slice(0, wordCount).join(' ');
};

interface PDFViewerProps {
  directUrl: string;
  pageNumber: number | null;
  textToHighlight: string | null;
}

const PDFViewer: React.FC<PDFViewerProps> = ({ directUrl, pageNumber, textToHighlight }) => {
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [documentLoaded, setDocumentLoaded] = useState<boolean>(false);

  // Store search props for direct access
  const searchPropsRef = useRef<RenderSearchProps | null>(null);

  const pageNavigationPluginInstance = pageNavigationPlugin();
  const { jumpToPage } = pageNavigationPluginInstance;

  const processedTextToHighlight = textToHighlight
    ? extractFirstWords(textToHighlight, 5)
    : undefined;
  const searchPluginInstance = searchPlugin({ keyword: processedTextToHighlight });
  const { Search } = searchPluginInstance;

  // Handle page navigation when document is loaded
  useEffect(() => {
    if (documentLoaded) {
      // Set a small delay to ensure the PDF is fully rendered
      const navigationTimeout = setTimeout(() => {
        if (pageNumber && pageNumber > 1) {
          try {
            jumpToPage(pageNumber);
          } catch (err) {
            console.error('Error navigating to page:', err);
          }
        }

        if (processedTextToHighlight) {
          setSearchQuery(processedTextToHighlight);
          handleSearch(processedTextToHighlight);
        }
      }, 400);

      return () => clearTimeout(navigationTimeout);
    }
  }, [documentLoaded, pageNumber, jumpToPage, processedTextToHighlight]);

  const handleSearch = (query: string) => {
    if (query.trim() && searchPropsRef.current) {
      searchPropsRef.current.clearKeyword();
      searchPropsRef.current.setKeyword(query);
      searchPropsRef.current.search();
    }
  };

  const handleDocumentLoad = () => {
    setDocumentLoaded(true);
  };

  return (
    <div className={styles.pdfViewerContainer}>
      <div className={styles.pdfToolbar}>
        <div className={styles.searchForm}>
          <TextInput
            value={searchQuery}
            onChange={(text) => setSearchQuery(text)}
            placeholder="Search in document"
          />
          <Button
            variant="primary"
            onClick={() => handleSearch(searchQuery)}
            icon={<IconSearch size={16} />}
            aria-label="Search"
          />
        </div>
      </div>

      <div className={styles.pdfViewerContent}>
        <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
          <div className={styles.searchContainer}>
            <Search>
              {(renderSearchProps) => {
                searchPropsRef.current = renderSearchProps;

                return (
                  <div className={styles.searchWrapper}>
                    {renderSearchProps.keyword && renderSearchProps.numberOfMatches > 0 && (
                      <div className={styles.searchResults}>
                        <span>
                          {renderSearchProps.currentMatch} of {renderSearchProps.numberOfMatches}
                        </span>
                        <Button
                          variant="secondary"
                          size="small"
                          onClick={() => renderSearchProps.jumpToPreviousMatch()}
                          icon={<IconCaretLeft size={16} />}
                          aria-label="Previous match"
                        />
                        <Button
                          variant="secondary"
                          size="small"
                          onClick={() => renderSearchProps.jumpToNextMatch()}
                          icon={<IconCaretRight size={16} />}
                          aria-label="Next match"
                        />
                      </div>
                    )}
                  </div>
                );
              }}
            </Search>
          </div>
          <Viewer
            fileUrl={directUrl}
            plugins={[pageNavigationPluginInstance, searchPluginInstance]}
            defaultScale={SpecialZoomLevel.PageFit}
            withCredentials={false}
            onDocumentLoad={handleDocumentLoad}
            renderError={(error) => {
              console.error('Error loading PDF:', error);
              return (
                <div className={styles.pdfErrorContainer}>
                  <h3>Error loading PDF</h3>
                  <p>Failed to load the PDF document. Please try again later.</p>
                  <Button
                    variant="primary"
                    onClick={() => window.open(directUrl, '_blank', 'noopener,noreferrer')}
                  >
                    Open PDF directly
                  </Button>
                </div>
              );
            }}
          />
        </Worker>
      </div>
    </div>
  );
};

export default PDFViewer;
